import {
  EmptyState,
  FilterSegments,
  ListRow,
  ListRows,
  Skeleton,
  formatDate,
  formatTime,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { useTemplateHistory, type Template } from './api'
import { historyActionLabel } from './labels'

/**
 * Aba Histórico: o log de alterações do documento (RN-DOC-46). Toda ação, com quem, quando e o
 * resumo. O texto do documento nunca aparece aqui; quem quer o texto compara versões.
 */
export function TemplateHistoryTab({ template }: { template: Template }) {
  const [filter, setFilter] = useState<'all' | 'versions'>('all')
  const [page, setPage] = useState(1)
  const history = useTemplateHistory(template.id, filter, page)
  const entries = history.data?.data ?? []

  return (
    <div className="flex max-w-[1120px] flex-col gap-4">
      <FilterSegments
        label="Filtrar o histórico"
        value={filter}
        onChange={(value) => {
          setFilter(value)
          setPage(1)
        }}
        options={[
          { value: 'all', label: 'Tudo' },
          { value: 'versions', label: 'Só versões' },
        ]}
      />

      {history.isError ? (
        <LoadError
          title="Não conseguimos carregar o histórico"
          error={history.error}
          onRetry={() => void history.refetch()}
        />
      ) : history.isPending ? (
        <div role="status" aria-label="Carregando" className="flex flex-col gap-2">
          <Skeleton className="h-[56px] w-full" />
          <Skeleton className="h-[56px] w-full" />
          <Skeleton className="h-[56px] w-full" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          title={filter === 'versions' ? 'Nenhuma versão publicada ainda' : 'Nada registrado ainda'}
          description="Cada alteração no documento fica registrada aqui, com quem fez e quando: criação, mudanças no rascunho, versões publicadas, vínculos e arquivamento."
        />
      ) : (
        <div>
          <ListRows>
            {entries.map((entry) => {
              const when = new Date(entry.occurredAt)
              return (
                <ListRow
                  key={entry.id}
                  title={historyActionLabel(entry.action)}
                  meta={
                    <>
                      {entry.summary ? <span className="text-ink">{entry.summary} · </span> : null}
                      {titleCase(entry.actor.name)} · {formatDate(when, { year: true })} às{' '}
                      {formatTime(when)}
                    </>
                  }
                />
              )
            })}
          </ListRows>
          {history.data ? (
            <ListFooter
              meta={history.data.meta}
              noun={['registro', 'registros']}
              note="do mais novo para o mais antigo"
              onPage={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}
